/* ============================================================================
   The API client.
   ----------------------------------------------------------------------------
   Every request goes through here so that three things are true everywhere and
   not merely usually:

     1. Cookies are sent. The session is an httpOnly cookie, so `credentials`
        is not optional — omit it on one call and that call is anonymous.
     2. Mutations carry the CSRF header. The API hands the token over in the
        body of login and /me, and it is kept in memory here; forgetting it on
        one form is a 403 nobody can explain later.
     3. Failures arrive as one Error type carrying the API's own message,
        which is written in Hinglish for a human. Showing "Request failed with
        status code 422" instead is how a dashboard becomes unusable.
   ========================================================================== */

/** Set at build time; empty in development, where Vite proxies /api. */
const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

const CSRF_HEADER = 'x-csrf-token';

const SAFE_METHODS = new Set(['GET', 'HEAD']);

/**
 * A full URL for an API path, for the places that are a LINK rather than a
 * fetch — a PDF, a data export — and so never pass through apiFetch.
 *
 * A bare "/api/..." href works in development, where Vite proxies it, and in
 * production opens the dashboard's own host, which has no API: the file
 * never arrives. Every href into the API goes through this.
 */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Per-field messages from a 422, keyed by field name. */
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  /** True when the session is gone and the only cure is logging in again. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/**
 * The CSRF token, in memory — NOT read from `document.cookie`.
 *
 * It used to be read from the cookie, which worked only in development. The
 * cookie (CSRF_COOKIE) is `__Host-` in production, so it belongs to the
 * API's host alone, and a page on another subdomain never sees it in
 * `document.cookie`. The Vite proxy hid that by putting this app and the API
 * on one host. Deployed, every mutation would have gone out with no header
 * and come back 403 — while login and every read kept working.
 *
 * So the API returns the token in the body of login and of /me, and any
 * response carrying one updates it here. Picked up in apiFetch rather than by
 * each caller, so no screen can forget to. A module variable, not storage:
 * nothing in this app goes in localStorage, and a reload asks /me again
 * anyway.
 */
let csrfToken: string | null = null;

function rememberCsrf(body: unknown): void {
  if (body && typeof body === 'object' && 'csrfToken' in body) {
    const token = (body as { csrfToken: unknown }).csrfToken;
    if (typeof token === 'string' && token) csrfToken = token;
  }
}

/** Called when the session ends, so a token outlives nothing it belonged to. */
export function forgetCsrfToken(): void {
  csrfToken = null;
}

interface ErrorBody {
  error?: { code?: string; message?: string; fields?: { field: string; message: string }[] };
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ErrorBody = {};
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    /* A non-JSON error body means something upstream failed — a proxy, or the
       API being down. The status is all we have, and it is enough. */
  }

  const fields: Record<string, string> = {};
  for (const item of body.error?.fields ?? []) fields[item.field] = item.message;

  return new ApiError(
    response.status,
    body.error?.code ?? 'UNKNOWN',
    body.error?.message ?? 'Something went wrong. Please try again in a moment.',
    fields,
  );
}

/* ----------------------------------------------------------------------------
   The 401 seam.
   ----------------------------------------------------------------------------
   A dead session shows up as a 401 on whichever request happened to be in
   flight, which could be any of them. Handling it at each call site means
   handling it at MOST call sites, and the one that gets missed is the one that
   leaves a stale dashboard on screen showing an endpoint error nobody can act
   on.

   So the client reports it once, here, and SessionProvider registers itself as
   the listener. A module-level callback rather than an import of the session
   module, because api.ts must not depend on React: it is also what the session
   module itself calls, and that would be a cycle.
   -------------------------------------------------------------------------- */

type UnauthorizedListener = () => void;

let onUnauthorized: UnauthorizedListener | null = null;

/** Register the session's `expire`. Returns an unsubscribe for the effect. */
export function setUnauthorizedListener(listener: UnauthorizedListener): () => void {
  onUnauthorized = listener;
  return () => {
    if (onUnauthorized === listener) onUnauthorized = null;
  };
}

/**
 * The auth endpoints answer 401 for reasons that are not "your session died".
 *
 *   /api/auth/me     401 IS the anonymous case, and SessionProvider is already
 *                    asking it that question on purpose.
 *   /api/auth/login  401 means the password was wrong. Firing the listener
 *                    would be harmless today but says something untrue.
 *
 * Everything else answering 401 means the session we thought we had is gone.
 */
function isSessionSignal(path: string): boolean {
  return !path.startsWith('/api/auth/');
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';

  if (!SAFE_METHODS.has(method)) {
    if (csrfToken) headers[CSRF_HEADER] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      // The session is an httpOnly cookie. Without this it is never sent.
      credentials: 'include',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // Distinguish "the API said no" from "the API is not there", because the
    // second is almost always a dev server that is not running.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK', 'Cannot reach the API. Is the server running?');
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (error.isUnauthorized && isSessionSignal(path)) onUnauthorized?.();
    throw error;
  }

  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json();
  rememberCsrf(body);
  return body as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiFetch<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  /* The permission matrix is the only PUT in this dashboard, and it is a PUT
     rather than a PATCH on purpose: the body carries a role's WHOLE set of
     grants, so two people on that screen cannot produce a result neither of
     them chose. See updateRoleSchema. */
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
